/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 *
 *
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.36.0
 */

import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import includes from 'lodash/includes';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { EFastActionsButtonTypes } from '@/main/components/FastActions/constants/constants';
import { useGetFastActionsQuery } from '@/main/components/FastActions/store';
import { EFastBasketSwitchboxContext, EFastWizardType } from '@/main/components/Table/constants';
import { EOperation } from '@/main/modules/app/appConstants';
import withAsyncStateManagement from '@/main/modules/asyncStateManagment/WithAsyncStateManagement';
import { wizardStorageClearStepsAction } from '@/main/modules/wizard/wizardActions';
import { openRoute } from '@/main/routes/constants/externalRoutes';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardBillsDoc,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { EWizardName, WIZARD_ALL_ENTITIES } from '@modules/wizard/wizardConstants';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';

import Title from '../../../components/common/Title/Title';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import Wizard, { IWizardProps } from '../../../modules/wizard/components/Wizard';
import PageBills from '../../PageBills/Loadable';
import { PageBillsType } from '../../PageBills/pageBillsTypes';
import PagePersonalAccount from '../../PagePersonalAccount/PagePersonalAccount';
import WizardBillsDocStepChoose from '../../WizardBillsDoc/components/WizardBillsDocStepChoose';
import WizardBillsDocStepConfirmation from '../../WizardBillsDoc/components/WizardBillsDocStepConfirmation';
import { wizardBillsDocHandleNextAction } from '../../WizardBillsDoc/wizardBillsDocActions';
import { EWizardBillsDocSteps } from '../../WizardBillsDoc/wizardBillsDocConsant';
import wizardBillsDocReducer from '../../WizardBillsDoc/wizardBillsDocReducer';
import { makeSelectWizardBillsDocIsStepValid } from '../../WizardBillsDoc/wizardBillsDocSelectors';

const WizardFastBillsDoc = ({ basketType, operation }: Pick<IWizardProps, 'basketType' | 'operation'>) => {

    const dispatch = useDispatch();
    const location = useLocation();

    const stepName = useSelector(makeSelectWizardStepName());
    const isStepValid = useSelector(makeSelectWizardBillsDocIsStepValid());
    const translationBillsDoc = useSelector(makeSelectTranslationWizardBillsDoc());
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const { instructionsLinks: { repsBasic } } = translationCommon;

    const { data: fastActionsData, isSuccess: isFastActionsDataSuccess } = useGetFastActionsQuery();

    const fastWizard = useMemo(() => {
        switch (operation) {
            case EOperation.BILL_INVOICE:
                return EFastWizardType.WIZARD_BILLS_DOCS;
            case EOperation.REPORT_DOCUMENTS:
                return EFastWizardType.WIZARD_REPORT_DOCS;
            default:
                return undefined;
        }
    },[operation]);

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardBillsDocSteps.AccountsChooseStep:
                return <PagePersonalAccount
                    isFastActionPage
                    basketContext={EFastBasketSwitchboxContext.WIZARD_BILLS_DOCS}
                    fastWizard={fastWizard}
                />;
            case EWizardBillsDocSteps.BillsChooseStep:
                return (
                    <PageBills
                        cookie={
                            {
                                BILL_PAGE: PageBillsType.BILLS,
                            } as any
                        }
                        match={{ params: { clientId: '' } }}
                        type={PageBillsType.BILLS}
                        />
                    );
            case EWizardBillsDocSteps.StepChoose:
                return <WizardBillsDocStepChoose/>;
            case EWizardBillsDocSteps.StepConfirmation:
                return <WizardBillsDocStepConfirmation/>;
        }
    }, [fastWizard, stepName]);

    const getStartData = useCallback(() => {
        return {
            operation,
        }
    },[operation]);

    useLayoutEffect(() => {
        dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.BILLS_DOC }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[]);

    useEffect(() => {
        if (location.pathname === RoutePath.wizardFastBillsDoc && isFastActionsDataSuccess) {
            if (!includes(fastActionsData?.buttons, EFastActionsButtonTypes.BILL_INVOICE)) {
                openRoute(RoutePath.dashboard);
            }
        }

        if (location.pathname === RoutePath.wizardFastReportDoc && isFastActionsDataSuccess) {
            if (!includes(fastActionsData?.buttons, EFastActionsButtonTypes.REPORT_DOCUMENTS)) {
                openRoute(RoutePath.dashboard);
            }
        }
    }, [fastActionsData, isFastActionsDataSuccess, location.pathname]);

    useEffect(() => {
        switch (stepName) {
            case EWizardBillsDocSteps.StepChoose:
                dispatch(
                    logBusinessEvent({
                        eventName: ELogBusinessEventName.B2B_BILLS_REPORTS_MASTER_ON_STEP_1,
                        type: ELogBusinessEventType.common,
                    }),
                );
                break;
            case EWizardBillsDocSteps.StepConfirmation:
                dispatch(
                    logBusinessEvent({
                        eventName: ELogBusinessEventName.B2B_BILLS_REPORTS_MASTER_ON_STEP_2,
                        type: ELogBusinessEventType.common,
                    }),
                );
        }
    }, [dispatch, stepName])

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.wizardBillsDoc,
                            TranslationFileNamesEnum.wizard,
                        ]}/>

            <Title pageName={ECommonTranslationPages.wizardBillsDoc}/>

            <Wizard
                basketType={basketType}
                entityId={WIZARD_ALL_ENTITIES}
                getStartData={getStartData}
                instructionLink={repsBasic}
                isNextDisabled={!isStepValid}
                name={EWizardName.BILLS_DOC}
                translationProps={{
                    error: translationBillsDoc.error,
                    steps: translationBillsDoc.steps,
                    title: translationBillsDoc.title,
                }}
                onWizardNextHandler={() => dispatch(wizardBillsDocHandleNextAction())}
            >
                {renderStep()}
            </Wizard>
        </Fragment>
    );
};

export default withAsyncStateManagement(
    connect()(WizardFastBillsDoc),
    { reducer: { wizardBillsDoc: wizardBillsDocReducer } },
);
